/**
 * "Export" on each section and chart of the interactive Leadership report
 * (Carl, via Rusty, 28 Oct), and the drill-in drawer's search and quick
 * filters: the pure parts (file names, a chart's numbers as CSV, which
 * computed styles a picture keeps, which tickets a search keeps), so they are
 * tested without a browser.
 */

/** The page's sections that have their own Excel sheets (`InsightsSection` on the server). */
export const EXPORT_SECTIONS = [
  "tiles",
  "teams",
  "status",
  "sla",
  "intake",
  "getting",
  "sites",
  "people",
  "moves",
] as const;
export type ExportSection = (typeof EXPORT_SECTIONS)[number];

/** `Tickets by site & country` → `tickets-by-site-country`: a safe, readable file name part. */
export function slug(text: string): string {
  const out = text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return out || "section";
}

/** `helpdesk-leadership-<what>-<first>-to-<last>.<ext>`. */
export function exportFilename(
  what: string,
  range: { first?: string | null; last?: string | null } | undefined,
  ext: "png" | "csv",
): string {
  const span = range?.first && range.last ? `-${range.first}-to-${range.last}` : "";
  return `helpdesk-leadership-${slug(what)}${span}.${ext}`;
}

/** The text a table cell shows, also for a cell that is a small React element. */
export function cellText(cell: unknown): string {
  if (cell === null || cell === undefined || typeof cell === "boolean") {
    return "";
  }
  if (typeof cell === "string" || typeof cell === "number" || typeof cell === "bigint") {
    return String(cell);
  }
  if (Array.isArray(cell)) {
    return cell.map(cellText).join("");
  }
  if (typeof cell === "object" && "props" in cell) {
    const props = (cell as { props?: { children?: unknown } }).props;
    return cellText(props?.children);
  }
  return "";
}

/**
 * One CSV cell, as `insights-helpers` writes them (kept here so this file
 * imports nothing and runs under `node --test`): quoted when needed, and a
 * leading = + - @ defused (spreadsheet formula injection).
 */
function csvCell(value: string): string {
  const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
  return /[",\n\r;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** A chart's "Data table" as CSV (BOM, CRLF, formula-safe), as the CSV downloads elsewhere. */
export function tableCsv(table: {
  columns: readonly string[];
  rows: readonly unknown[][];
}): string {
  const lines = [table.columns.map((c) => csvCell(c)).join(",")];
  for (const row of table.rows) {
    lines.push(row.map((cell) => csvCell(cellText(cell))).join(","));
  }
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

/**
 * Computed style properties a picture does not copy onto its elements, so the
 * copy lays itself out again once the hidden controls are gone: heights (a
 * fixed height would keep the gap where the "Show details" toggle was; an
 * SVG or empty box keeps it, see `keepsStyle`),
 * transitions, animations, the pointer, and an SVG path's shape (`d`: already
 * in its attribute).
 */
const SKIPPED_STYLE = new Set([
  "d",
  "height",
  "block-size",
  "transition",
  "transition-property",
  "transition-duration",
  "transition-delay",
  "transition-timing-function",
  "animation",
  "animation-name",
  "animation-duration",
  "animation-delay",
  "cursor",
]);

/**
 * Widths a picture always writes: with no border style the browser computes a
 * border width of 0 whatever was set, so "0px" looks like the default, yet
 * the app's reset gives every element a solid style, under which the default
 * width is 3px (thick black frames round everything).
 */
export function alwaysWritten(property: string): boolean {
  return /^(border-(top|right|bottom|left)-width|outline-width|column-rule-width)$/.test(property);
}

/**
 * Whether a picture copies this computed style property. An SVG element and
 * an empty box (a legend swatch, a bar) keep their height: nothing inside
 * them would give them one.
 */
export function keepsStyle(property: string, fixedHeight: boolean): boolean {
  if (fixedHeight && (property === "height" || property === "block-size")) {
    return true;
  }
  return !SKIPPED_STYLE.has(property);
}

// ── The drill-in drawer's search and quick filters (Rusty, 28 Oct) ─────────

export interface TicketFilter {
  /** Typed text: every word must appear in the ticket's number, subject, team, status… */
  query: string;
  kinds: readonly string[];
  groups: readonly string[];
  teams: readonly string[];
}

export const NO_TICKET_FILTER: TicketFilter = { query: "", kinds: [], groups: [], teams: [] };

interface FilterableTicket {
  number?: number | null;
  kind: string;
  subject?: string;
  team: string;
  status: string;
  status_group: string;
  channel: string;
  offering?: string;
  service?: string;
  category?: string;
  priority?: string;
  site_label?: string;
  country?: string;
}

/** What the search box looks in, folded. */
export function ticketSearchText(row: FilterableTicket, extra: readonly string[] = []): string {
  return fold(
    [
      row.number ?? "",
      row.kind,
      row.subject ?? "",
      row.team,
      row.status,
      row.status_group,
      row.channel,
      row.offering ?? "",
      row.service ?? "",
      row.category ?? "",
      row.priority ?? "",
      row.site_label ?? "",
      row.country ?? "",
      ...extra,
    ].join(" "),
  );
}

function fold(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function hasTicketFilter(filter: TicketFilter): boolean {
  return (
    filter.query.trim() !== "" ||
    filter.kinds.length > 0 ||
    filter.groups.length > 0 ||
    filter.teams.length > 0
  );
}

/** The tickets the drawer's search and quick filters keep (order kept). */
export function filterTickets<T extends FilterableTicket>(
  rows: readonly T[],
  filter: TicketFilter,
  extra: (row: T) => readonly string[] = () => [],
): T[] {
  const words = fold(filter.query).split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    if (filter.kinds.length > 0 && !filter.kinds.includes(row.kind)) {
      return false;
    }
    if (filter.groups.length > 0 && !filter.groups.includes(row.status_group)) {
      return false;
    }
    if (filter.teams.length > 0 && !filter.teams.includes(row.team)) {
      return false;
    }
    if (words.length === 0) {
      return true;
    }
    const text = ticketSearchText(row, extra(row));
    return words.every((word) => text.includes(word));
  });
}

/** The distinct values of one field, sorted, for a quick filter's choices. */
export function distinct<T>(rows: readonly T[], pick: (row: T) => string): string[] {
  return [...new Set(rows.map(pick))].sort((a, b) => a.localeCompare(b));
}
